/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { CarouselProvider, Slider, Slide, CarouselContext } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { HotelCard } from '../HotelCard';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { MapViewSliderProps } from './MapViewSlider.types';
import { StyledMapViewSlider } from './MapViewSlider.styles';
import { eventUtil } from '@marriott/mi-ui-library';
import { miniCardWidth, smallMobileMiniCardWidth } from '../../modules/constants/constant';

const ActiveSlideTracker = (props: any) => {
  const carouselContext = useContext(CarouselContext);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!carouselContext) return;
    const onSlideChange = () => {
      if (isFirstRender.current) {
        isFirstRender.current = false; // Skip effect on initial render
        return;
      }
      const updatedCurrentSlide = Math.ceil(carouselContext.state.currentSlide);
      const updatedActiveMarker = props?.markersData?.filter(
        (marker: any) =>
          marker?.node?.basicInformation?.latitude ===
            props?.searchData[updatedCurrentSlide]?.basicInformation?.latitude &&
          marker?.node?.basicInformation?.longitude ===
            props?.searchData[updatedCurrentSlide]?.basicInformation?.longitude
      );
      if (updatedCurrentSlide !== props?.currentSlideIndex) {
        props?.setCurrentSlideIndex(updatedCurrentSlide);
        props?.setSelectedMarker(updatedActiveMarker[0]);
        eventUtil.dispatch('updateActiveMarker', updatedActiveMarker[0]);
      }
    };
    carouselContext.subscribe(onSlideChange);
    return () => carouselContext.unsubscribe(onSlideChange);
  }, [carouselContext, props?.currentSlideIndex]);

  return null;
};

// Use named rather than default exports.
export const MapViewSlider: FC<MapViewSliderProps> = (props: any) => {
  const [currentSlide, setCurrentSlide] = useState(
    props?.isMobileView ? (props?.currentSlideIndex >= 0 ? props?.currentSlideIndex : 0) : 0
  );

  const [animationClass, setAnimationClass] = useState('');
  const [visibleSlides, setVisibleSlides] = useState(1);

  useEffect(() => {
    const slides = Math.max((props?.containerWidth - 32) / 340, 1);
    setVisibleSlides(slides);
  }, [props?.containerWidth]);

  useEffect(() => {
    setAnimationClass('.no-animation');
    setCurrentSlide(props?.currentSlideIndex);

    setTimeout(() => {
      setAnimationClass('');
    }, 10);
  }, [props?.currentSlideIndex]);

  return (
    <StyledMapViewSlider
      data-testid="luxury-MapViewSlider"
      data-component-name="m-luxury-MapViewSlider"
      containerWidth={props?.containerWidth}
      miniCardLength={props?.searchResults?.propertiesByIds?.length}
      viewport={props?.viewPort}
    >
      <div className="d-flex flex-column carousel-container ml-3">
        <CarouselProvider
          naturalSlideWidth={props?.viewPort === 'Small Mobile' ? smallMobileMiniCardWidth : miniCardWidth}
          naturalSlideHeight={112}
          totalSlides={props?.searchResults?.propertiesByIds?.length}
          visibleSlides={visibleSlides}
          currentSlide={currentSlide}
          isIntrinsicHeight={true}
          infinite={false}
        >
          <ActiveSlideTracker
            setCurrentSlide={setCurrentSlide}
            searchData={props?.searchResults?.propertiesByIds}
            markersData={props?.markersData}
            activeMarker={props?.activeMarker}
            setSelectedMarker={props?.setSelectedMarker}
            setCurrentSlideIndex={props?.setCurrentSlideIndex}
            currentSlideIndex={props?.currentSlideIndex}
          />

          <Slider className="carousel-slider" classNameAnimation={animationClass}>
            {props?.searchResults?.propertiesByIds?.map((property: any, index: number) => (
              <Slide index={index} key={index}>
                <div className="slide-container d-flex align-items-stretch justify-content-center">
                  <HotelCard
                    props={property}
                    currentLocale={'en-US'}
                    ctaLabel={props?.ctaLabel}
                    showReviews={props?.showReviews}
                    viewPort={props?.viewPort ?? 'Mobile'}
                  />
                </div>
              </Slide>
            ))}
          </Slider>
        </CarouselProvider>
      </div>
    </StyledMapViewSlider>
  );
};
